import React, { ReactElement, Ref, SyntheticEvent } from 'react';
import { Input, MaskedInput } from 'rsuite';
import { phoneMask } from '../../../common/validation';
import { FORM_INPUT_TYPES } from '../../../common/enums';
import { BaseTextInputWithErrorStatus } from './types';

interface TextInputWithErrorStatusProps extends BaseTextInputWithErrorStatus {
  id?: string;
  placeholder?: string;
  ref?: Ref<HTMLDivElement>;
  ariaInvalid?: boolean;
  ariaDescribedby?: string;
  ariaLabel?: string;
}

const removePhoneFormatting = (val: string): string => val?.replace(/\D+/g, '');

export const CustomTextInput = React.memo(
  ({
    id,
    autoComplete,
    ariaInvalid,
    ariaDescribedby,
    ariaLabel,
    disabled,
    inputFieldType,
    inputMode,
    name,
    onChange,
    placeholder,
    preventHtmlValidation,
    trimEndOnChange,
    trimStartOnChange,
    type,
    value,
    style,
  }: TextInputWithErrorStatusProps): ReactElement => {
    const changedInternal = (v: string, e: SyntheticEvent): void => {
      let result: string | number | undefined = v;
      if (trimStartOnChange) {
        result = result.trimStart();
      }
      if (trimEndOnChange) {
        result = result.trimEnd();
      }

      // remove any masks prior to saving to form values
      if (type === FORM_INPUT_TYPES.PHONE) {
        result = removePhoneFormatting(v);
      } else if (type === FORM_INPUT_TYPES.NUMBER) {
        if (result === '') {
          result = undefined;
        } else {
          result = Number(result);
        }
      }
      if (onChange) onChange(result, e);
    };

    return (
      <>
        {type === FORM_INPUT_TYPES.PHONE ? (
          <MaskedInput
            id={id}
            aria-invalid={ariaInvalid || undefined}
            aria-describedby={ariaDescribedby}
            aria-label={ariaLabel}
            autoComplete={autoComplete}
            placeholder={placeholder}
            onChange={changedInternal}
            value={value}
            style={{ backgroundColor: 'white' }}
            mask={phoneMask}
            disabled={disabled}
          />
        ) : (
          <Input
            id={id}
            aria-invalid={ariaInvalid || undefined}
            aria-describedby={ariaDescribedby}
            aria-label={ariaLabel}
            autoComplete={autoComplete}
            name={name}
            placeholder={placeholder}
            onChange={changedInternal}
            style={{ backgroundColor: 'white', ...style }}
            type={inputFieldType}
            {...(inputFieldType === 'textarea'
              ? { as: 'textarea', rows: 5 }
              : {})}
            {...(inputMode ? { inputMode } : {})}
            {...(preventHtmlValidation ? { noValidate: true } : {})}
            value={value}
            disabled={disabled}
            step="any"
          />
        )}
      </>
    );
  }
);

CustomTextInput.displayName = 'CustomTextInput';
